import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Row, Col, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { shipmentService } from '../../services/shipment.service';
import { carrierAccountService } from '../../services/carrierAccount.service';
import { CARRIER_BILLITING_STATUS } from '../../constant';
import AlertDialogModal from '../common/AlertDialog';
import LoaderSpinner from '../common/Loader';
import './CarrierIntegration.css';

// Carrier Forms Configuration
const CARRIER_FORMS = {
    shipvisionDetails: [
        {
            id: 'reference',
            label: 'Reference',
            placeholder: 'e.g. DHL-MAIN',
            type: 'text',
            required: true,
            section: 'ShipVision Details',
            helperText: '',
        },
    ],
    productionCredentials: [
        {
            id: 'clientId',
            label: 'Client ID',
            placeholder: 'From DHL onboarding email',
            type: 'text',
            required: true,
            section: 'Production Credentials',
            helperText: '',
        },
        {
            id: 'clientSecret',
            label: 'Client Secret',
            placeholder: 'From DHL onboarding email',
            type: 'password',
            required: true,
            section: 'Production Credentials',
            helperText: 'Masked after save - Never stored in plain text',
        },
        {
            id: 'distributionCenter',
            label: 'Distribution Center',
            placeholder: 'Select distribution center',
            type: 'select',
            required: true,
            section: 'Production Credentials',
            helperText: '',
            options: [
                { value: 'dc1', label: 'Distribution Center 1' },
                { value: 'dc2', label: 'Distribution Center 2' },
            ],
        },
        {
            id: 'pickupId',
            label: 'Pickup ID',
            placeholder: 'DHL-assigned pickup account',
            type: 'text',
            required: true,
            section: 'Production Credentials',
            helperText: '',
        },
    ],
    testCredentials: [
        {
            id: 'testClientId',
            label: 'Test Client ID',
            placeholder: 'Sandbox Client ID',
            type: 'text',
            required: false,
            section: 'Test Credentials',
            helperText: 'Optional - DHL sandbox environment',
        },
        {
            id: 'testClientSecret',
            label: 'Test Client Secret',
            placeholder: 'Sandbox Client Secret',
            type: 'password',
            required: false,
            section: 'Test Credentials',
            helperText: 'Optional - DHL sandbox environment',
        },
        {
            id: 'testDistributionCenter',
            label: 'Test Distribution Center',
            placeholder: 'Select test distribution center',
            type: 'select',
            required: false,
            section: 'Test Credentials',
            helperText: 'Optional - DHL sandbox environment',
            options: [
                { value: 'test_dc1', label: 'Test Distribution Center 1' },
                { value: 'test_dc2', label: 'Test Distribution Center 2' },
            ],
        },
        {
            id: 'testPickupId',
            label: 'Test Pickup ID',
            placeholder: 'Sandbox Pickup ID',
            type: 'text',
            required: false,
            section: 'Test Credentials',
            helperText: 'Optional - DHL sandbox environment',
        },
    ],
};

// The API serializes some endpoints in PascalCase and others in camelCase,
// so lower-case the first letter of every key before reading a row.
const normalizeKeys = row =>
    Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), value]));

// Pulls the list out of a list response regardless of envelope casing.
// The API returns rows under EntityList/entityList (or entity), and some endpoints
// return a bare array — so check each shape before giving up.
const extractEntityList = data => {
    const payload = normalizeKeys(data);
    if (Array.isArray(payload.entityList)) return payload.entityList;
    if (Array.isArray(payload.entity)) return payload.entity;
    if (Array.isArray(data)) return data;
    return [];
};

// Reads schema.fields off a carrier row, preferring the camelCase schemaJson string
const parseSchemaFields = carrier => {
    if (carrier.schemaJson) {
        try {
            return JSON.parse(carrier.schemaJson).fields || [];
        } catch {
            // Fall through to the object form below
        }
    }
    const schema = normalizeKeys(carrier.schema);
    return schema.fields || [];
};

// Maps a GetAllCarrierCompany row to the shape the wallet list renders.
// The API can return the same carrier company more than once, so callers dedupe by id.
const mapCarrierCompanyToWalletCarrier = row => {
    const company = normalizeKeys(row);
    return {
        carrier_id: String(company.id),
        carrier_name: company.carrierCompanyName || '',
        carrier_logo: company.logo || '',
        enabled: Boolean(company.isActive),
    };
};

const dedupeWalletCarriers = carriers => {
    const byId = new Map();
    carriers.forEach(carrier => {
        if (!byId.has(carrier.carrier_id)) {
            byId.set(carrier.carrier_id, carrier);
        }
    });
    return Array.from(byId.values());
};

// Statuses that mean an account finished connecting. The API isn't consistent about the
// exact string (and a freshly connected account can come back with a blank status), so we
// match a set of connected-like values and treat an empty status as connected too.
const CONNECTED_STATUSES = new Set(['connected', 'active', 'success', 'succeeded', 'enabled']);
const isConnectedStatus = status => {
    const normalized = (status || '').trim().toLowerCase();
    return normalized === '' || CONNECTED_STATUSES.has(normalized);
};

// Turns the account's Credentials array into a { credentialKey: maskedValue } lookup
// so the details view can read values by key (account_id, client_id, client_secret, ...)
const buildCredentialMap = (rawCredentials = []) =>
    (rawCredentials || []).reduce((map, entry) => {
        const cred = normalizeKeys(entry);
        if (cred.credentialKey) {
            map[cred.credentialKey] = cred.maskedValue ?? '';
        }
        return map;
    }, {});

// Maps a CarrierAccount/Accounts row to the shape the accounts list renders
const mapCarrierAccount = row => {
    const account = normalizeKeys(row);
    return {
        account_id: String(account.id),
        carrier_id: account.carrierCompanyId != null ? String(account.carrierCompanyId) : '',
        carrier_name: account.carrierName || '',
        carrier_logo: '',
        account_name: account.accountName || '',
        status: account.status || '',
        is_default: Boolean(account.isDefault),
        enabled: Boolean(account.isActive),
        // Masked credential values keyed by CredentialKey, for the View Details modal
        credentials: buildCredentialMap(account.credentials),
    };
};

// Maps a CarrierAccount/Carriers row to the shape the Add Carrier grid renders
const mapAvailableCarrier = row => {
    const carrier = normalizeKeys(row);
    return {
        carrier_id: String(carrier.id),
        carrier_key: carrier.carrierKey || '',
        carrier_company_id: carrier.carrierCompanyId != null ? String(carrier.carrierCompanyId) : '',
        carrier_billing_type_id: carrier.carrierBillingTypeId != null ? String(carrier.carrierBillingTypeId) : '',
        carrier_name: carrier.carrierName || '',
        carrier_logo: '',
        description: carrier.description || '',
        is_connected: Boolean(carrier.isConnected),
        enabled: Boolean(carrier.isActive),
        // Field definitions the credentials step can render instead of the hardcoded CARRIER_FORMS
        schema_fields: parseSchemaFields(carrier),
    };
};

// Carrier Tile Component
const CarrierTile = ({ carrier, isSelected, onToggle }) => {
    return (
        <button
            key={carrier.carrier_id}
            className="col-lg-2 col-md-3 col-sm-4 col-6 sortBtn carrier-tile-btn"
            onClick={() => onToggle(carrier.carrier_id)}
        >
            <div className={`carrier-tile-card ${isSelected ? 'selected' : ''}`}>
                {carrier.carrier_logo && (
                    <div className="carrier-tile-logo-wrap">
                        <img
                            className="carrier-tile-logo"
                            src={carrier.carrier_logo}
                            alt={carrier.carrier_name}
                            onError={e => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                )}
                <div className="carrier-tile-name">{carrier.carrier_name}</div>
                {carrier.description && <div className="carrier-tile-desc">{carrier.description}</div>}
            </div>
        </button>
    );
};

CarrierTile.propTypes = {
    carrier: PropTypes.shape({
        carrier_id: PropTypes.string.isRequired,
        description: PropTypes.string,
        carrier_logo: PropTypes.string,
        carrier_name: PropTypes.string.isRequired,
    }).isRequired,
    isSelected: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

// Carrier Grid Component
const CarrierGrid = ({ carriers, selectedCarriers, onToggle, searchTerm, isLoading, error }) => {
    const term = searchTerm.toLowerCase();
    const filteredCarriers = carriers.filter(
        carrier =>
            (carrier.carrier_name || '').toLowerCase().includes(term) ||
            (carrier.description || '').toLowerCase().includes(term)
    );

    if (isLoading) {
        return (
            <div className="carrier-state-msg carrier-state-msg--loading">
                <LoaderSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="carrier-state-msg carrier-state-msg--error">
                <p>{error}</p>
            </div>
        );
    }

    if (filteredCarriers.length === 0) {
        return (
            <div className="carrier-state-msg">
                <p>No carriers available to connect.</p>
            </div>
        );
    }

    return (
        <div className="integrationListWrapper row">
            {filteredCarriers.map(carrier => {
                const isSelected = selectedCarriers.includes(carrier.carrier_id);
                return (
                    <CarrierTile
                        key={carrier.carrier_id}
                        carrier={carrier}
                        isSelected={isSelected}
                        onToggle={onToggle}
                    />
                );
            })}
        </div>
    );
};

CarrierGrid.propTypes = {
    carriers: PropTypes.arrayOf(
        PropTypes.shape({
            carrier_id: PropTypes.string.isRequired,
            carrier_logo: PropTypes.string,
            carrier_name: PropTypes.string.isRequired,
        })
    ).isRequired,
    selectedCarriers: PropTypes.arrayOf(PropTypes.string).isRequired,
    onToggle: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
};

// Add Carrier Modal Component
const AddCarrierModal = ({
    show,
    onHide,
    carriers,
    selectedCarriers,
    setSelectedCarriers,
    carrierSearchTxt,
    setCarrierSearchTxt,
    onAddCarrier,
}) => {
    const toggleCarrier = carrierId => {
        setSelectedCarriers([carrierId]);
        setCarrierSearchTxt('');
        onAddCarrier();
    };

    const handleClose = () => {
        setSelectedCarriers([]);
        setCarrierSearchTxt('');
        onHide(false);
    };

    return (
        <Modal
            show={show}
            size="lg"
            scrollable
            aria-labelledby="contained-modal-title-vcenter"
            centered
            onHide={handleClose}
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">Add Carrier Account</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search carrier by name"
                        value={carrierSearchTxt}
                        onChange={e => setCarrierSearchTxt(e.target.value)}
                    />
                </div>
                <CarrierGrid
                    carriers={carriers}
                    selectedCarriers={selectedCarriers}
                    onToggle={toggleCarrier}
                    searchTerm={carrierSearchTxt}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

AddCarrierModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    carriers: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedCarriers: PropTypes.arrayOf(PropTypes.string).isRequired,
    setSelectedCarriers: PropTypes.func.isRequired,
    carrierSearchTxt: PropTypes.string.isRequired,
    setCarrierSearchTxt: PropTypes.func.isRequired,
    onAddCarrier: PropTypes.func.isRequired,
};

// Field Row Component Helper
const FieldRow = ({ field, currentCredentials, handleInputChange, hasError }) => {
    const controlClass = `form-control${hasError ? ' requiredthis' : ''}`;

    return (
        <Col key={field.id} xs={12} md={6} className="mb-3 mb-md-0">
            <InputGroup>
                <div className="input-label-Created-date carrier-field-label">
                    {field.label}
                    {field.required && <span className="require-field">*</span>}
                </div>
                <div className="input-box">
                    {field.type === 'select' ? (
                        <select
                            value={currentCredentials[field.id] || ''}
                            onChange={e => handleInputChange(field.id, e.target.value)}
                            className={`${controlClass} noMarginBtm`}
                        >
                            <option value="">Select...</option>
                            {field.options?.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type={field.type}
                            placeholder={field.placeholder}
                            value={currentCredentials[field.id] || ''}
                            onChange={e => handleInputChange(field.id, e.target.value)}
                            onBlur={e => {
                                const trimmed = e.target.value.trim();
                                if (trimmed !== e.target.value) {
                                    handleInputChange(field.id, trimmed);
                                }
                            }}
                            className={controlClass}
                            // Keep the browser password manager from treating these credential
                            // fields as a login form (which then autofills the page search boxes)
                            autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                            data-lpignore="true"
                            data-form-type="other"
                        />
                    )}
                </div>
                {hasError && <div className="requiredthis">*This is required</div>}
                {!hasError && field.helperText && <p className="carrier-field-helper">{field.helperText}</p>}
            </InputGroup>
        </Col>
    );
};

FieldRow.propTypes = {
    field: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        placeholder: PropTypes.string,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        section: PropTypes.string,
        helperText: PropTypes.string,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
            })
        ),
    }).isRequired,
    currentCredentials: PropTypes.object.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    hasError: PropTypes.bool,
};

// Carrier Credentials Modal Component
const CarrierCredentialsModal = ({
    show,
    onHide,
    selectedCarrierId,
    selectedCarrierName,
    carrierCredentials,
    setCarrierCredentials,
    onValidate,
}) => {
    const handleInputChange = (fieldId, value) => {
        setCarrierCredentials(prev => ({
            ...prev,
            [selectedCarrierId]: {
                ...prev[selectedCarrierId],
                [fieldId]: value,
            },
        }));
    };

    const handleValidate = () => {
        onValidate();
    };

    const handleClose = () => {
        onHide(false);
    };

    const currentCredentials = carrierCredentials[selectedCarrierId] || {};
    const allFields = [
        ...CARRIER_FORMS.shipvisionDetails,
        ...CARRIER_FORMS.productionCredentials,
        ...CARRIER_FORMS.testCredentials,
    ];
    const sections = ['ShipVision Details', 'Production Credentials', 'Test Credentials'];

    const renderSectionFields = section => {
        const sectionFields = allFields.filter(field => field.section === section);
        const fieldRows = [];
        // Two per row to match the md={6} columns FieldRow renders
        for (let i = 0; i < sectionFields.length; i += 2) {
            fieldRows.push(sectionFields.slice(i, i + 2));
        }
        return fieldRows.map((fieldsInRow, rowIndex) => (
            <Row key={`${section}-row-${rowIndex}`} className="mb-2 p-2 rowBottomSpace">
                {fieldsInRow.map(field => (
                    <FieldRow
                        key={field.id}
                        field={field}
                        currentCredentials={currentCredentials}
                        handleInputChange={handleInputChange}
                    />
                ))}
            </Row>
        ));
    };

    return (
        <Modal
            show={show}
            size="lg"
            scrollable
            aria-labelledby="carrier-credentials-modal-title"
            centered
            onHide={handleClose}
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton>
                <Modal.Title id="carrier-credentials-modal-title">Add Your {selectedCarrierName} Account</Modal.Title>
                <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 0 0' }}>
                    Enter credentials from your {selectedCarrierName} onboarding emails
                </p>
            </Modal.Header>
            <Modal.Body>
                <div className="cus-content-only-sec">
                    {sections.map(section => (
                        <div key={section}>
                            <Row className="mb-2 p-2 rowBottomSpace">
                                <Col xs={12}>
                                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#0071dc' }}>
                                        {section}
                                    </h6>
                                </Col>
                            </Row>
                            {renderSectionFields(section)}
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleValidate}>
                    Validate & Connect
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

CarrierCredentialsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    selectedCarrierId: PropTypes.string,
    selectedCarrierName: PropTypes.string,
    carrierCredentials: PropTypes.object.isRequired,
    setCarrierCredentials: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
};

// A single read-only labelled field in the account details modal
const DetailField = ({ label, children }) => (
    <Col xs={12} md={6} className="mb-3 mb-md-0">
        <div>
            <div
                style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#999',
                    display: 'block',
                    marginBottom: '6px',
                }}
            >
                {label}
            </div>
            {children}
        </div>
    </Col>
);

DetailField.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
};

// Read-only value box for a DetailField
const DetailValue = ({ value }) => (
    <div
        style={{
            padding: '10px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            fontSize: '14px',
            wordBreak: 'break-all',
        }}
    >
        {value || '—'}
    </div>
);

DetailValue.propTypes = {
    value: PropTypes.string,
};

// View Account Details Modal Component
// Renders the selected account's masked credentials straight from GET /Accounts
const ViewAccountDetailsModal = ({ show, onHide, account, onEditCredentials }) => {
    const handleClose = () => {
        onHide(false);
    };

    const credentials = account?.credentials || {};
    const connected = isConnectedStatus(account?.status);
    const statusColor = connected ? '#4CAF50' : '#999';
    const statusLabel = account?.status
        ? account.status.charAt(0).toUpperCase() + account.status.slice(1)
        : 'Connected';

    return (
        <Modal
            show={show}
            size="lg"
            scrollable
            aria-labelledby="account-details-modal-title"
            centered
            onHide={handleClose}
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton>
                <div>
                    <Modal.Title id="account-details-modal-title" style={{ fontSize: '18px', fontWeight: '600' }}>
                        {account?.carrier_name || 'Carrier'} Account
                    </Modal.Title>
                    <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0', marginBottom: 0 }}>
                        {account?.account_name || `Account-${account?.account_id || ''}`}
                    </p>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-3">
                    <DetailField label="ACCOUNT ID">
                        <DetailValue value={credentials.account_id} />
                    </DetailField>
                    <DetailField label="CLIENT ID">
                        <DetailValue value={credentials.client_id} />
                    </DetailField>
                </Row>

                <Row className="mb-3">
                    <DetailField label="CLIENT SECRET">
                        <DetailValue value={credentials.client_secret} />
                    </DetailField>
                    <DetailField label="DISTRIBUTION CENTER">
                        <DetailValue value={credentials.distribution_center} />
                    </DetailField>
                </Row>

                <Row className="mb-3">
                    <DetailField label="PICKUP ID">
                        <DetailValue value={credentials.pickup_id} />
                    </DetailField>
                    <DetailField label="Account Name">
                        <DetailValue value={account?.account_name} />
                    </DetailField>
                </Row>

                <Row className="mb-3">
                    <DetailField label="STATUS">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: statusColor,
                                }}
                            />
                            <span style={{ fontSize: '14px', color: statusColor, fontWeight: '500' }}>
                                {statusLabel}
                            </span>
                        </div>
                    </DetailField>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ViewAccountDetailsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    account: PropTypes.object,
    onEditCredentials: PropTypes.func.isRequired,
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, onTabChange }) => {
    return (
        <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '20px' }}>
            <button
                className="cus-tab-btn"
                onClick={() => onTabChange('wallet')}
                style={{
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: activeTab === 'wallet' ? '600' : '400',
                    color: activeTab === 'wallet' ? '#0071dc' : '#666',
                    border: 'none',
                    background: 'none',
                    borderBottom: activeTab === 'wallet' ? '2px solid #0071dc' : 'none',
                    cursor: 'pointer',
                    marginBottom: '-2px',
                }}
            >
                Wallet Carriers
            </button>
            <button
                className="cus-tab-btn"
                onClick={() => onTabChange('accounts')}
                style={{
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: activeTab === 'accounts' ? '600' : '400',
                    color: activeTab === 'accounts' ? '#0071dc' : '#666',
                    border: 'none',
                    background: 'none',
                    borderBottom: activeTab === 'accounts' ? '2px solid #0071dc' : 'none',
                    cursor: 'pointer',
                    marginBottom: '-2px',
                }}
            >
                My Carrier Accounts
            </button>
        </div>
    );
};

TabNavigation.propTypes = {
    activeTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
};

// Carrier Card Component (Wallet Carriers)
const CarrierCard = ({ carrier }) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                // Wrap so the trailing status/actions drop below the name on narrow screens
                flexWrap: 'wrap',
                gap: '12px',
                padding: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginBottom: '12px',
                backgroundColor: '#fff',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 220px', minWidth: 0 }}>
                <div>
                    <h6 style={{ margin: '0', fontWeight: '600', color: '#000' }}>{carrier.carrier_name}</h6>
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 12px',
                    borderRadius: '4px',
                }}
            >
                <span
                    style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: carrier.enabled ? '#4CAF50' : '#999',
                    }}
                />
                <span style={{ fontSize: '12px', fontWeight: '500', color: carrier.enabled ? '#4CAF50' : '#999' }}>
                    {carrier.enabled ? 'Enabled' : 'Disabled'}
                </span>
            </div>
        </div>
    );
};

CarrierCard.propTypes = {
    carrier: PropTypes.shape({
        carrier_id: PropTypes.string.isRequired,
        carrier_name: PropTypes.string.isRequired,
        carrier_logo: PropTypes.string,
        enabled: PropTypes.bool.isRequired,
    }).isRequired,
};

// Account Card Component (My Carrier Accounts)
const AccountCard = ({ account, onEdit, onDelete, isDisconnecting }) => {
    const connected = isConnectedStatus(account.status);
    const statusColor = connected ? '#4CAF50' : '#999';
    const statusLabel = account.status
        ? account.status.charAt(0).toUpperCase() + account.status.slice(1)
        : 'Connected';
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                // Wrap so the trailing status/actions drop below the name on narrow screens
                flexWrap: 'wrap',
                gap: '12px',
                padding: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginBottom: '12px',
                backgroundColor: '#fff',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 220px', minWidth: 0 }}>
                <div>
                    <h6 style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#000' }}>{account.carrier_name}</h6>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px' }}>
                        <p style={{ margin: '0', fontSize: '12px', color: '#666', wordBreak: 'break-word' }}>
                            {account.account_name}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span
                                style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: statusColor,
                                }}
                            />
                            <span style={{ fontSize: '12px', color: statusColor, fontWeight: '500' }}>{statusLabel}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <button
                    onClick={() => onEdit(account)}
                    style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        background: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                    }}
                >
                    <i className="fa fa-eye" aria-hidden="true" style={{ marginRight: '4px' }} /> View
                </button>
                <button
                    onClick={() => onDelete(account)}
                    disabled={isDisconnecting}
                    style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        background: '#fff',
                        borderRadius: '4px',
                        cursor: isDisconnecting ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: '#d32f2f',
                        opacity: isDisconnecting ? 0.6 : 1,
                    }}
                >
                    {' '}
                    <i className="fa fa-chain-broken" aria-hidden="true" style={{ marginRight: '4px' }} />{' '}
                    {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
            </div>
        </div>
    );
};

AccountCard.propTypes = {
    account: PropTypes.shape({
        carrier_id: PropTypes.string,
        carrier_name: PropTypes.string.isRequired,
        carrier_logo: PropTypes.string,
        account_id: PropTypes.string.isRequired,
        account_name: PropTypes.string,
        status: PropTypes.string,
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isDisconnecting: PropTypes.bool,
};

// Wallet Carriers List Component
const WalletCarriersList = ({ carriers, searchTerm, isLoading, error }) => {
    const term = searchTerm.toLowerCase();
    const filteredCarriers = carriers.filter(
        carrier => carrier.enabled && (carrier.carrier_name || '').toLowerCase().includes(term)
    );

    if (isLoading) {
        return (
            <div className="carrier-state-msg carrier-state-msg--loading">
                <LoaderSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="carrier-state-msg carrier-state-msg--error">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div>
            {filteredCarriers.length === 0 ? (
                <div className="carrier-state-msg">
                    <p>No wallet carriers found matching your search.</p>
                </div>
            ) : (
                filteredCarriers.map(carrier => <CarrierCard key={carrier.carrier_id} carrier={carrier} />)
            )}
        </div>
    );
};

WalletCarriersList.propTypes = {
    carriers: PropTypes.arrayOf(PropTypes.object).isRequired,
    searchTerm: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
};

// My Carrier Accounts List Component
const MyCarrierAccountsList = ({ accounts, searchTerm, isLoading, error, onEdit, onDelete, disconnectingAccountId }) => {
    const term = searchTerm.toLowerCase();
    const filteredAccounts = accounts.filter(
        account =>
            (account.carrier_name || '').toLowerCase().includes(term) ||
            (account.account_name || '').toLowerCase().includes(term) ||
            (account.account_id || '').toLowerCase().includes(term)
    );

    if (isLoading) {
        return (
            <div className="carrier-state-msg carrier-state-msg--loading">
                <LoaderSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="carrier-state-msg carrier-state-msg--error">
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div>
            {filteredAccounts.length === 0 ? (
                <div className="carrier-state-msg">
                    <p>No carrier accounts found matching your search.</p>
                </div>
            ) : (
                filteredAccounts.map(account => (
                    <AccountCard
                        key={account.account_id}
                        account={account}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        isDisconnecting={disconnectingAccountId === account.account_id}
                    />
                ))
            )}
        </div>
    );
};

MyCarrierAccountsList.propTypes = {
    accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
    searchTerm: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    disconnectingAccountId: PropTypes.string,
};

// Schema fields that belong on the account itself rather than in the credentials bag.
// Every other schema field is sent through as a credential keyed by its fieldKey,
// so a new carrier works without code changes as long as its schema follows this convention.
const ACCOUNT_LEVEL_FIELDS = {
    nickname: 'accountName',
    accountName: 'accountName',
    description: 'description',
};

// Builds the POST /Connect/{carrierKey} body from the carrier schema and the entered values
const buildConnectPayload = (carrier, values) => {
    const payload = {
        carrierCompanyId: Number(carrier.carrier_company_id) || 0,
        accountName: '',
        description: '',
        isDefault: false,
        credentials: {},
    };

    // DHL eCommerce is billed directly through the carrier, so it needs an explicit
    // billing type on the account. Every other carrier omits the key.
    if (carrier.carrier_key === 'dhl_ecommerce') {
        payload.carrierBillingTypeId = CARRIER_BILLITING_STATUS.CARRIER_DIRECT;
    }

    (carrier.schema_fields || []).forEach(field => {
        const value = values[field.fieldKey] || '';
        const accountProp = ACCOUNT_LEVEL_FIELDS[field.fieldKey];
        if (accountProp) {
            payload[accountProp] = value;
        } else {
            payload.credentials[field.fieldKey] = value;
        }
    });

    return payload;
};

// Pulls the human-readable message out of a Connect response or error body
const readStatusMessage = body => {
    const data = normalizeKeys(body);
    const message = normalizeKeys(data.message);
    return message.appStatusDescription || '';
};

// Turns the carrier's schema.fields into the shape FieldRow renders, ordered by displayOrder.
// Description is no longer captured in the UI, so it is dropped even if the schema still sends it.
const buildFormFields = (schemaFields = []) =>
    schemaFields
        .filter(field => field.fieldKey !== 'description')
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map(field => ({
            id: field.fieldKey,
            label: field.label || field.fieldKey,
            placeholder: field.placeholder || '',
            type: field.type || 'text',
            required: Boolean(field.required),
            helperText: '',
            options: field.options || [],
        }));

// Merged Carrier Onboarding Modal Component (Step 1: Select Carrier → Step 2: Enter Credentials)
// Declared at module scope so typing in the search box re-renders it instead of remounting it
const CarrierOnboardingModal = ({
    show,
    onHide,
    modalStep: step,
    setModalStep: setStep,
    carriers,
    isLoading,
    error,
    schemaFields,
    isConnecting,
    selectedCarriers,
    setSelectedCarriers,
    carrierSearchTxt,
    setCarrierSearchTxt,
    selectedCarrierId: carrierId,
    selectedCarrierName: carrierName,
    carrierCredentials,
    setCarrierCredentials,
    onValidate,
    onSelectCarrier,
}) => {
    const [fieldErrors, setFieldErrors] = useState({});

    const handleSelectCarrier = id => {
        setSelectedCarriers([id]);
        onSelectCarrier(id);
        setFieldErrors({});
        setStep(2); // Auto-advance to step 2
    };

    const handleInputChange = (fieldId, value) => {
        setCarrierCredentials(prev => ({
            ...prev,
            [carrierId]: {
                ...prev[carrierId],
                [fieldId]: value,
            },
        }));
        // Clear the field's error as soon as it has a value
        if (value) {
            setFieldErrors(prev => ({ ...prev, [fieldId]: false }));
        }
    };

    const handleClose = () => {
        setStep(1); // Reset to step 1
        setSelectedCarriers([]);
        setCarrierSearchTxt('');
        setFieldErrors({});
        onHide(false);
    };

    const handleBack = () => {
        setStep(1); // Go back to step 1
        setFieldErrors({});
    };

    const currentCredentials = carrierCredentials[carrierId] || {};
    const formFields = buildFormFields(schemaFields);

    const handleValidate = () => {
        const missing = formFields.filter(
            field => field.required && !String(currentCredentials[field.id] || '').trim()
        );

        setFieldErrors(Object.fromEntries(missing.map(field => [field.id, true])));

        if (missing.length > 0) {
            toast.error(`Please fill the required field${missing.length > 1 ? 's' : ''}: ${missing
                .map(field => field.label)
                .join(', ')}`);
            return;
        }

        onValidate();
    };

    const renderFieldRows = () => {
        const fieldRows = [];
        for (let i = 0; i < formFields.length; i += 2) {
            fieldRows.push(formFields.slice(i, i + 2));
        }
        return fieldRows.map(fieldsInRow => (
            <Row key={`field-row-${fieldsInRow[0].id}`} className="mb-3 p-2 rowBottomSpace">
                {fieldsInRow.map(field => (
                    <FieldRow
                        key={field.id}
                        field={field}
                        currentCredentials={currentCredentials}
                        handleInputChange={handleInputChange}
                        hasError={Boolean(fieldErrors[field.id])}
                    />
                ))}
            </Row>
        ));
    };

    return (
        <Modal
            show={show}
            size="lg"
            scrollable
            aria-labelledby="carrier-onboarding-modal-title"
            centered
            onHide={handleClose}
            className="generateKey modalDeleteWrapper"
        >
            {/* STEP 1: Carrier Selection */}
            {step === 1 && (
                <>
                    <Modal.Header closeButton>
                        <div className="carrier-modal-header">
                            <Modal.Title id="carrier-onboarding-modal-title" className="carrier-modal-title">
                                Add Carrier Account
                            </Modal.Title>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="carrier-modal-body">
                        <div className="mb-4">
                            <input
                                type="search"
                                name="carrierNameSearch"
                                className="form-control carrier-modal-search"
                                placeholder="Search carrier by name"
                                value={carrierSearchTxt}
                                onChange={e => setCarrierSearchTxt(e.target.value)}
                                autoComplete="off"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                        </div>
                        <CarrierGrid
                            carriers={carriers}
                            selectedCarriers={selectedCarriers}
                            onToggle={handleSelectCarrier}
                            searchTerm={carrierSearchTxt}
                            isLoading={isLoading}
                            error={error}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    </Modal.Footer>
                </>
            )}

            {/* STEP 2: Enter Credentials */}
            {step === 2 && (
                <>
                    <Modal.Header closeButton>
                        <div className="carrier-modal-header">
                            <Modal.Title id="carrier-onboarding-modal-title" className="carrier-modal-title">
                                Add Your {carrierName} Account
                            </Modal.Title>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="cus-content-only-sec formWrapperCarrierBody">
                            {formFields.length === 0 ? (
                                <div className="carrier-state-msg">
                                    <p>No credential fields configured for this carrier.</p>
                                </div>
                            ) : (
                                <div className="carrier-form-section">
                                    <Row className="mb-2 p-2 rowBottomSpace">
                                        <Col xs={12}>
                                            <h6 className="carrier-form-section-title">Credentials</h6>
                                        </Col>
                                    </Row>
                                    {renderFieldRows()}
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleBack} disabled={isConnecting}>
                            Back
                        </Button>
                        <Button variant="secondary" onClick={handleClose} disabled={isConnecting}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleValidate} disabled={isConnecting}>
                            {isConnecting ? 'Connecting...' : 'Validate & Connect'}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};

CarrierOnboardingModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    modalStep: PropTypes.number.isRequired,
    setModalStep: PropTypes.func.isRequired,
    carriers: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    schemaFields: PropTypes.arrayOf(PropTypes.object),
    isConnecting: PropTypes.bool,
    selectedCarriers: PropTypes.arrayOf(PropTypes.string).isRequired,
    setSelectedCarriers: PropTypes.func.isRequired,
    carrierSearchTxt: PropTypes.string.isRequired,
    setCarrierSearchTxt: PropTypes.func.isRequired,
    selectedCarrierId: PropTypes.string,
    selectedCarrierName: PropTypes.string,
    carrierCredentials: PropTypes.object.isRequired,
    setCarrierCredentials: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    onSelectCarrier: PropTypes.func.isRequired,
};

/**
 * CarrierIntegration Component
 * Manages carrier integration settings and configuration
 */
export default function CarrierIntegration() {
    const navigate = useNavigate();
    const [searchTxt, setSearchTxt] = useState('');
    const [showAddCarrierModal, setShowAddCarrierModal] = useState(false);
    const [selectedCarriers, setSelectedCarriers] = useState([]);
    const [selectedCarrierId, setSelectedCarrierId] = useState(null);
    const [carrierSearchTxt, setCarrierSearchTxt] = useState('');
    const [carrierCredentials, setCarrierCredentials] = useState({});
    const [activeTab, setActiveTab] = useState('wallet');
    const [walletCarriers, setWalletCarriers] = useState([]);
    const [walletLoading, setWalletLoading] = useState(false);
    const [walletError, setWalletError] = useState('');
    const [carrierAccounts, setCarrierAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [availableCarriers, setAvailableCarriers] = useState([]);
    const [availableLoading, setAvailableLoading] = useState(false);
    const [availableError, setAvailableError] = useState('');
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [showAccountDetailsModal, setShowAccountDetailsModal] = useState(false);
    const [modalStep, setModalStep] = useState(1); // Step 1: Carrier selection, Step 2: Credentials
    const [disconnectingAccountId, setDisconnectingAccountId] = useState(null);
    const [accountToDisconnect, setAccountToDisconnect] = useState(null);

    const fetchWalletCarriers = useCallback(() => {
        setWalletLoading(true);
        setWalletError('');
        shipmentService
            .fetchAllCarriers({})
            .then(response => {
                const list = response?.data?.EntityList || [];
                setWalletCarriers(dedupeWalletCarriers(list.map(mapCarrierCompanyToWalletCarrier)));
            })
            .catch(() => {
                setWalletCarriers([]);
                setWalletError('Unable to load carriers. Please try again.');
            })
            .finally(() => {
                setWalletLoading(false);
            });
    }, []);

    const fetchCarrierAccounts = useCallback(() => {
        setAccountsLoading(true);
        setAccountsError('');
        carrierAccountService
            .getCarrierAccountsService()
            .then(response => {
                const list = extractEntityList(response?.data);
                setCarrierAccounts(list.map(mapCarrierAccount));
            })
            .catch(() => {
                setCarrierAccounts([]);
                setAccountsError('Unable to load carrier accounts. Please try again.');
            })
            .finally(() => {
                setAccountsLoading(false);
            });
    }, []);

    const fetchAvailableCarriers = useCallback(() => {
        setAvailableLoading(true);
        setAvailableError('');
        carrierAccountService
            .getAvailableCarriersService()
            .then(response => {
                const list = extractEntityList(response?.data);
                setAvailableCarriers(list.map(mapAvailableCarrier).filter(carrier => carrier.enabled));
            })
            .catch(() => {
                setAvailableCarriers([]);
                setAvailableError('Unable to load carriers. Please try again.');
            })
            .finally(() => {
                setAvailableLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchWalletCarriers();
        fetchCarrierAccounts();
    }, [fetchWalletCarriers, fetchCarrierAccounts]);

    // Refresh the connectable carrier list every time the Add Carrier modal opens
    useEffect(() => {
        if (showAddCarrierModal) {
            fetchAvailableCarriers();
        }
    }, [showAddCarrierModal, fetchAvailableCarriers]);

    // "My Carrier Accounts" only lists accounts that finished connecting
    const connectedAccounts = useMemo(
        () => carrierAccounts.filter(account => isConnectedStatus(account.status)),
        [carrierAccounts]
    );

    // The Add Carrier modal only offers carriers that aren't connected yet
    const connectableCarriers = useMemo(
        () => availableCarriers.filter(carrier => !carrier.is_connected),
        [availableCarriers]
    );

    // Closes the Add Carrier modal and clears every field the form touched
    const resetAddCarrierModal = useCallback(() => {
        setShowAddCarrierModal(false);
        setModalStep(1);
        setSelectedCarriers([]);
        setSelectedCarrier(null);
        setSelectedCarrierId(null);
        setCarrierSearchTxt('');
        setCarrierCredentials({});
        // Refresh the accounts list on every close so an account connected in this
        // session shows up even if the success handler's refresh was missed
        fetchCarrierAccounts();
    }, [fetchCarrierAccounts]);

    const handleSearchChange = e => {
        setSearchTxt(e.target.value);
    };

    const handleBlur = e => {
        setSearchTxt(e.target.value.trim());
    };

    const handleEnterKey = e => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        // Search functionality to be implemented
        console.log('Searching for:', searchTxt);
    };

    const handleClearSearch = () => {
        setSearchTxt('');
    };

    const handleAddCarrier = carrierId => {
        const carrier = connectableCarriers.find(c => c.carrier_id === carrierId);
        if (carrier) {
            setSelectedAccount(null);
            setSelectedCarrier(carrier);
            setSelectedCarrierId(carrierId);
        }
    };

    const handleValidateCarrier = () => {
        if (!selectedCarrier) {
            toast.error('Select a carrier before connecting.');
            return;
        }

        const values = carrierCredentials[selectedCarrierId] || {};
        setConnecting(true);

        carrierAccountService
            .connectCarrierAccountService(selectedCarrier.carrier_key, buildConnectPayload(selectedCarrier, values))
            .then(response => {
                const data = normalizeKeys(response?.data);
                if (data.status && data.status !== 200) {
                    // The backend reports auth failures with a 200 transport status and a failed body
                    toast.error(readStatusMessage(response?.data) || 'Unable to connect the account.');
                    return;
                }

                toast.success(readStatusMessage(response?.data) || 'Carrier account connected successfully.');

                // Connected accounts move to the accounts tab, and the carrier drops off the Add list
                fetchCarrierAccounts();
                fetchAvailableCarriers();
                setActiveTab('accounts');
                setShowAddCarrierModal(false);
                setModalStep(1);
                setSelectedCarriers([]);
                setSelectedCarrier(null);
                setCarrierSearchTxt('');
                setCarrierCredentials(prev => ({ ...prev, [selectedCarrierId]: {} }));
            })
            .catch(err => {
                toast.error(
                    readStatusMessage(err?.response?.data) || 'Unable to connect the account. Please try again.'
                );
            })
            .finally(() => {
                setConnecting(false);
            });
    };

    const handleEditAccount = account => {
        setSelectedCarrier(null);
        setSelectedAccount(account);
        setSelectedCarrierId(account.carrier_id);
        setShowAccountDetailsModal(true);
    };

    const handleOpenEditCredentials = carrierId => {
        setShowAccountDetailsModal(false);
        setSelectedCarrierId(carrierId);
        setShowAddCarrierModal(true);
        setModalStep(2); // Jump directly to credentials form
    };

    // Disconnect click just opens the confirmation dialog; the API call happens on confirm
    const handleDeleteAccount = account => {
        setAccountToDisconnect(account);
    };

    const handleCancelDisconnect = () => {
        if (disconnectingAccountId) return; // Don't let the user dismiss mid-request
        setAccountToDisconnect(null);
    };

    const handleConfirmDisconnect = () => {
        if (!accountToDisconnect) return;
        const account = accountToDisconnect;
        setDisconnectingAccountId(account.account_id);

        carrierAccountService
            .disconnectCarrierAccountService(account.account_id)
            .then(response => {
                const data = normalizeKeys(response?.data);
                if (data.status && data.status !== 200) {
                    // A found-but-failed disconnect can still arrive with a 200 transport status
                    toast.error(readStatusMessage(response?.data) || 'Unable to disconnect the account.');
                    return;
                }

                toast.success(readStatusMessage(response?.data) || 'Carrier account disconnected successfully.');

                // The account drops off the accounts tab and the carrier becomes connectable again
                fetchCarrierAccounts();
                fetchAvailableCarriers();
                setAccountToDisconnect(null);
            })
            .catch(err => {
                if (err?.response?.status === 404) {
                    toast.error(readStatusMessage(err?.response?.data) || 'Carrier account not found.');
                } else {
                    toast.error(
                        readStatusMessage(err?.response?.data) ||
                            'Unable to disconnect the account. Please try again.'
                    );
                }
            })
            .finally(() => {
                setDisconnectingAccountId(null);
            });
    };

    return (
        <main>
            <div className="main__container">
                {/* Header Section */}
                <div className="main__tile_heading cus-btn-include-sec">
                    Carrier Integration
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            <button
                                className="btn cus-seconday-bg-btn top-btn mt-2"
                                onClick={() => setShowAddCarrierModal(true)}
                            >
                                Add Carrier
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn mt-2"
                                onClick={() => navigate('/setting')}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search Bar Section */}
                <div className="filterContainer show searchBox mb-2">
                    <div className="searchInputBox">
                        <input
                            type="search"
                            name="carrierAccountSearch"
                            value={searchTxt}
                            onBlur={handleBlur}
                            onKeyDown={handleEnterKey}
                            onChange={handleSearchChange}
                            className="form-control"
                            placeholder="Search by carrier name or ID"
                            // Stop the browser password manager from autofilling this box with a
                            // saved username/email, which would silently filter the whole list out.
                            // readOnly blocks Chrome autofill; focus (user click) makes it editable.
                            autoComplete="off"
                            data-lpignore="true"
                            data-form-type="other"
                            readOnly
                            // readOnly blocks autofill but Bootstrap greys the field out; keep it
                            // looking like a normal editable input (white bg, text cursor).
                            style={{ backgroundColor: '#fff', cursor: 'text' }}
                            onFocus={e => e.target.removeAttribute('readonly')}
                        />
                    </div>
                    <button className="btn cus-seconday-bg-btn ml-2 searchBtn" onClick={handleSearch}>
                        <i className="fa fa-search" aria-hidden="true"></i> Search
                    </button>
                    <button
                        disabled={!searchTxt}
                        className="btn cus-primary-transparent-btn ml-2 clearbtn"
                        onClick={handleClearSearch}
                    >
                        Clear Search
                    </button>
                </div>

                

                <div className="main_content cus-page-content-sec">
                    <div className="cus-content-only-sec" style={{ padding: '20px 0' }}>
                        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

                        {activeTab === 'wallet' ? (
                            <WalletCarriersList
                                carriers={walletCarriers}
                                searchTerm={searchTxt}
                                isLoading={walletLoading}
                                error={walletError}
                            />
                        ) : (
                            <MyCarrierAccountsList
                                accounts={connectedAccounts}
                                searchTerm={searchTxt}
                                isLoading={accountsLoading}
                                error={accountsError}
                                onEdit={handleEditAccount}
                                onDelete={handleDeleteAccount}
                                disconnectingAccountId={disconnectingAccountId}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Merged Carrier Onboarding Modal - Step 1 (Select) & Step 2 (Credentials) */}
            <CarrierOnboardingModal
                show={showAddCarrierModal}
                onHide={resetAddCarrierModal}
                modalStep={modalStep}
                setModalStep={setModalStep}
                carriers={connectableCarriers}
                isLoading={availableLoading}
                error={availableError}
                schemaFields={selectedCarrier?.schema_fields || []}
                isConnecting={connecting}
                selectedCarriers={selectedCarriers}
                setSelectedCarriers={setSelectedCarriers}
                carrierSearchTxt={carrierSearchTxt}
                setCarrierSearchTxt={setCarrierSearchTxt}
                selectedCarrierId={selectedCarrierId}
                selectedCarrierName={selectedCarrier?.carrier_name || selectedAccount?.carrier_name || ''}
                carrierCredentials={carrierCredentials}
                setCarrierCredentials={setCarrierCredentials}
                onValidate={handleValidateCarrier}
                onSelectCarrier={handleAddCarrier}
            />

            {/* View Account Details Modal */}
            <ViewAccountDetailsModal
                show={showAccountDetailsModal}
                onHide={setShowAccountDetailsModal}
                account={selectedAccount}
                onEditCredentials={handleOpenEditCredentials}
            />

            {/* Disconnect Confirmation Alert */}
            <AlertDialogModal
                modalShow={Boolean(accountToDisconnect)}
                modalClass="modalDeleteWrapper"
                headingText="DISCONNECT"
                messageText={
                    accountToDisconnect ? (
                        <>
                            Are you sure you want to disconnect &quot;
                            {accountToDisconnect.carrier_name || accountToDisconnect.account_name}&quot;?
                        </>
                    ) : (
                        ''
                    )
                }
                handleYesDeleteAlert={handleConfirmDisconnect}
                handleCancelDeleteAlert={handleCancelDisconnect}
                loader={Boolean(disconnectingAccountId)}
            />
        </main>
    );
}
